import { Component, EventEmitter, Input, OnChanges, Output, ChangeDetectionStrategy } from '@angular/core';
import { Currency } from '../../shared/account/Currency';

export interface PurchaseListItem {
  key: string | number;
  date: Date;
  amount: number;
}

export interface PurchaseRow extends PurchaseListItem {
  hasAmount: boolean;
}

export interface PurchaseListView {
  rows: PurchaseRow[];
  avgOrderValue: number;
  hasAnyAmount: boolean;
}

/** The average is over priced orders only: some sites sell without sending a value. */
export function buildPurchaseListView(purchases: PurchaseListItem[], totalRevenue: number): PurchaseListView {
  const rows = (purchases ?? []).map((purchase) => ({ ...purchase, hasAmount: purchase.amount > 0 }));
  const pricedCount = rows.filter((row) => row.hasAmount).length;
  return {
    rows,
    avgOrderValue: pricedCount > 0 ? totalRevenue / pricedCount : 0,
    hasAnyAmount: pricedCount > 0,
  };
}

@Component({
  selector: 'app-data-platform-purchases-list',
  templateUrl: './data-platform-purchases-list.component.html',
  styleUrls: ['./data-platform-purchases-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DataPlatformPurchasesListComponent implements OnChanges {
  @Input() purchases: PurchaseListItem[] = [];
  @Input() totalRevenue = 0;
  @Input() currency: Currency | undefined;
  @Input() isLoading = false;
  /** Renders total / avg order value / order count as summary cards instead of a single total row. */
  @Input() showSummaryCards = false;
  /** Makes the purchase rows clickable; each click emits purchaseSelected. */
  @Input() interactive = false;
  @Output() purchaseSelected = new EventEmitter<PurchaseListItem>();

  protected rows: PurchaseRow[] = [];
  protected avgOrderValue = 0;
  protected hasAnyAmount = false;
  protected rowRole: 'button' | null = null;
  protected rowTabindex: 0 | null = null;

  ngOnChanges() {
    const view = buildPurchaseListView(this.purchases, this.totalRevenue);
    this.rows = view.rows;
    this.avgOrderValue = view.avgOrderValue;
    this.hasAnyAmount = view.hasAnyAmount;
    this.rowRole = this.interactive ? 'button' : null;
    this.rowTabindex = this.interactive ? 0 : null;
  }

  protected onRowClick(purchase: PurchaseListItem) {
    if (this.interactive) {
      this.purchaseSelected.emit(purchase);
    }
  }
}
